import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import Button from '@/components/ui/button';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

interface UserInfoFormProps {
	onSubmit: (name: string, email: string, shareDetailsConsent: boolean) => void;
}

interface UserInfoFormValues {
	name: string;
	email: string;
	consent: boolean;
	shareDetailsConsent: boolean;
}

const validationSchema = Yup.object({
	name: Yup.string()
		.trim()
		.required('Name is required')
		.min(2, 'Name must be at least 2 characters')
		.max(50, 'Name must be less than 50 characters'),
	email: Yup.string()
		.trim()
		.required('Email is required')
		.email('Please enter a valid email address')
		.max(100, 'Email must be less than 100 characters'),
	consent: Yup.boolean(),
	shareDetailsConsent: Yup.boolean(),
});

const initialValues: UserInfoFormValues = {
	name: '',
	email: '',
	consent: true,
	shareDetailsConsent: true,
};

export default function UserInfoForm({ onSubmit }: UserInfoFormProps) {
	const handleUserInfoSubmit = async (values: UserInfoFormValues) => {
		try {
			if (values.consent) {
				// Create promotional user
				await haggleApiService.createPromotionalUser({
					email: values.email,
					name: values.name,
					consent: true,
				});
			}

			// Call the parent's onSubmit callback
			onSubmit(values.name, values.email, values.shareDetailsConsent);
		} catch (error) {
			if (
				error instanceof Error &&
				error.message.includes('Email already exists')
			) {
				onSubmit(values.name, values.email, values.shareDetailsConsent);
				return;
			} else {
				notificationService.error(`Failed to submit form. Please try again.`);
			}
		}
	};

	return (
		<div className="w-full h-full overflow-y-auto bg-gray-50">
			<div className="flex items-center justify-center min-h-full py-2 px-4">
				<div className="bg-white rounded-lg shadow-lg p-4 max-w-md w-full">
					<div className="text-center mb-6">
						<h2 className="text-xl font-semibold text-gray-800 mb-2">
							Before we get started
						</h2>
						<p className="text-gray-600 text-sm">
							Please provide your information so we can reply to you if you
							leave the chat.
						</p>
					</div>

					<Formik
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={handleUserInfoSubmit}
					>
						{({ isSubmitting }) => (
							<Form className="space-y-4">
								<div>
									<Field
										type="text"
										name="name"
										placeholder="Name"
										className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
										aria-label="Name"
										tabIndex={0}
									/>
									<ErrorMessage
										name="name"
										component="div"
										className="text-red-500 text-sm mt-1"
									/>
								</div>

								<div>
									<Field
										type="email"
										name="email"
										placeholder="Email Address"
										className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
										aria-label="Email Address"
										tabIndex={0}
									/>
									<ErrorMessage
										name="email"
										component="div"
										className="text-red-500 text-sm mt-1"
									/>
								</div>

								<div className="flex items-start gap-3">
									<Field
										type="checkbox"
										name="consent"
										id="consent"
										className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
										tabIndex={0}
										aria-label="Consent to special email promotions"
									/>
									<label htmlFor="consent" className="text-xs text-gray-700">
										Opt in to get special email promotions and updates. You can
										opt out anytime.
									</label>
								</div>

								<div className="flex items-start gap-3">
									<Field
										type="checkbox"
										name="shareDetailsConsent"
										id="shareDetailsConsent"
										className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
										tabIndex={0}
										aria-label="Consent to share details with merchants"
									/>
									<label
										htmlFor="shareDetailsConsent"
										className="text-xs text-gray-700"
									>
										Allow us to share your email with merchants for better
										service and follow-up.
									</label>
								</div>

								<Button
									type="submit"
									disabled={isSubmitting}
									className="w-full py-3 px-6 bg-purpleBrand-500 hover:bg-purpleBrand-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
									shape="none"
									transition="none"
								>
									{isSubmitting ? 'Starting chat...' : 'Start chat'}
								</Button>
							</Form>
						)}
					</Formik>
				</div>
			</div>
		</div>
	);
}
